/**
 * @fileoverview AR-924 — Client-side mirror of the backend upload rules (`Deliverables::FileValidation`):
 * photos, PDF, audio and video by MIME type, documents by extension, up to 50 MB each. Documents go by
 * extension because browsers disagree on the MIME type they send for Office and iWork files (often
 * `application/octet-stream`). The backend re-validates anyway.
 */

const BYTES_PER_MB = 1024 * 1024
export const MAX_FILE_SIZE_MB = 50
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * BYTES_PER_MB

const MEDIA_MIME = /^(image|audio|video)\/|^application\/pdf$/
/** Same list as the backend `DOCUMENT_EXTENSIONS`. */
export const DOCUMENT_EXTENSIONS: readonly string[] = [
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp',
  'pages', 'numbers', 'key', 'txt', 'rtf', 'csv', 'md',
]

export type FileRejectionReason = 'too_large' | 'type_not_allowed'

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : ''
}

export function isAllowedFileType(file: Pick<File, 'name' | 'type'>): boolean {
  return MEDIA_MIME.test(file.type) || DOCUMENT_EXTENSIONS.includes(extensionOf(file.name))
}

export function rejectFile(file: File): FileRejectionReason | null {
  if (!isAllowedFileType(file)) return 'type_not_allowed'
  if (file.size > MAX_FILE_SIZE_BYTES) return 'too_large'
  return null
}

/** Mirrors FileData::FileInteractor `file_type`. */
export type FileKind = 'image' | 'video' | 'audio' | 'file'

export function fileKindOf(mimeType: string): FileKind {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  return 'file'
}

const SIZE_DECIMALS = 1

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes >= BYTES_PER_MB) return `${(bytes / BYTES_PER_MB).toFixed(SIZE_DECIMALS)} MB`
  const KB = 1024
  if (bytes >= KB) return `${Math.round(bytes / KB)} KB`
  return `${bytes} B`
}
